import { liveQuery } from 'dexie';
import { db, SYNC_STATUS, ENTITY_TYPES, hashData } from '../db';

const ENTITY_CONFIG = {
  [ENTITY_TYPES.ORDER]: { table: db.orders, tableName: 'orders', idField: 'orderId' },
  [ENTITY_TYPES.EXPENSE]: { table: db.expenses, tableName: 'expenses', idField: 'expenseId' },
  [ENTITY_TYPES.CUSTOMER]: { table: db.customers, tableName: 'customers', idField: 'customerId' },
  [ENTITY_TYPES.PRODUCT]: { table: db.products, tableName: 'products', idField: 'productId' },
  [ENTITY_TYPES.MONEY_ACCOUNT]: { table: db.moneyAccounts, tableName: 'moneyAccounts', idField: 'accountId' },
  [ENTITY_TYPES.CATEGORY]: { table: db.categories, tableName: 'categories', idField: 'categoryId' },
  [ENTITY_TYPES.ORG_ACCOUNT]: { table: db.orgAccounts, tableName: 'orgAccounts', idField: 'accountId' }
};

const METADATA_LAST_SYNC_PREFIX = 'lastSync:';

class DataService {
  constructor() {
    this._initialized = false;
  }

  async ensureInitialized() {
    if (this._initialized) return;
    await db.open();
    this._initialized = true;
  }

  getEntityConfig(entityType) {
    const config = ENTITY_CONFIG[entityType];
    if (!config) {
      throw new Error(`No entity configuration found for ${entityType}`);
    }
    return config;
  }

  async getEntities(entityType, { orgId, accountId } = {}) {
    await this.ensureInitialized();
    const { table } = this.getEntityConfig(entityType);
    if (!orgId && !accountId) {
      return table.toArray();
    }

    if (orgId && accountId && this._hasIndex(table, '[orgId+accountId]')) {
      return table.where('[orgId+accountId]').equals([orgId, accountId]).toArray();
    }

    if (orgId) {
      return table.where('orgId').equals(orgId).toArray();
    }

    if (accountId) {
      return table.where('accountId').equals(accountId).toArray();
    }

    return table.toArray();
  }

  async getEntity(entityType, id) {
    await this.ensureInitialized();
    const { table, idField } = this.getEntityConfig(entityType);
    if (!id) return null;
    return table.get(id) || table.where(idField).equals(id).first();
  }

  async upsertFromServer(entityType, records = [], { orgId } = {}) {
    await this.ensureInitialized();
    const { table, idField } = this.getEntityConfig(entityType);
    const now = Date.now();

    await db.transaction('rw', table, async () => {
      for (const record of records) {
        if (!record || typeof record !== 'object') continue;
        const id = record[idField];
        if (!id) continue;

        const existing = await table.get(id);
        const serverModifiedAt = this._resolveServerTimestamp(record, existing, now);
        const normalized = {
          ...record,
          orgId: record.orgId || orgId || existing?.orgId || null,
          syncStatus: SYNC_STATUS.SYNCED,
          serverModifiedAt,
          dataHash: hashData(record),
          conflictData: null,
          updatedDate: serverModifiedAt,
          localModifiedAt: existing?.localModifiedAt || serverModifiedAt,
          createdDate: existing?.createdDate || serverModifiedAt
        };

        if (existing && existing.syncStatus && existing.syncStatus !== SYNC_STATUS.SYNCED) {
          const existingHash = existing.dataHash || hashData(existing);
          const hasDifferences = existingHash !== normalized.dataHash;

          if (hasDifferences) {
            await table.update(id, {
              conflictData: record,
              serverModifiedAt,
              dataHash: normalized.dataHash,
              syncStatus: SYNC_STATUS.CONFLICT
            });
          } else {
            await table.put({ ...(existing || {}), ...normalized, conflictData: null });
          }
        } else {
          await table.put({ ...(existing || {}), ...normalized });
        }
      }
    });

    return { count: Array.isArray(records) ? records.length : 0 };
  }

  async upsertLocal(entityType, data, { syncStatus = SYNC_STATUS.PENDING_CREATE } = {}) {
    await this.ensureInitialized();
    const { table, idField } = this.getEntityConfig(entityType);
    const now = Date.now();
    const id = data[idField];
    const existing = id ? await table.get(id) : null;

    const localData = {
      ...(existing || {}),
      ...data,
      syncStatus,
      localModifiedAt: now,
      updatedDate: now,
      createdDate: existing?.createdDate || now,
      dataHash: hashData({ ...(existing || {}), ...data })
    };

    const newId = await table.put(localData);
    return { id: newId, data: localData };
  }

  async markAsSynced(entityType, id, serverData) {
    await this.ensureInitialized();
    const { table } = this.getEntityConfig(entityType);
    if (!id) return;

    const now = Date.now();
    const updates = {
      syncStatus: SYNC_STATUS.SYNCED,
      localModifiedAt: now,
      serverModifiedAt: this._resolveServerTimestamp(serverData, null, now),
      conflictData: null
    };

    if (serverData) {
      updates.dataHash = hashData(serverData);
      updates.updatedDate = updates.serverModifiedAt;
    }

    await table.update(id, updates);
  }

  async removeEntity(entityType, id) {
    await this.ensureInitialized();
    const { table } = this.getEntityConfig(entityType);
    if (!id) return;
    await table.delete(id);
  }

  async setSyncStatus(entityType, id, status, serverData = null) {
    await this.ensureInitialized();
    const { tableName } = this.getEntityConfig(entityType);
    if (!id) return;
    await db.updateSyncStatus(tableName, id, status, serverData);
  }

  subscribe(entityType, callback, filter = {}) {
    const { table } = this.getEntityConfig(entityType);
    const queryFn = () => {
      if (filter.orgId && filter.accountId && this._hasIndex(table, '[orgId+accountId]')) {
        return table.where('[orgId+accountId]').equals([filter.orgId, filter.accountId]).toArray();
      }
      if (filter.orgId) {
        return table.where('orgId').equals(filter.orgId).toArray();
      }
      if (filter.accountId) {
        return table.where('accountId').equals(filter.accountId).toArray();
      }
      return table.toArray();
    };

    const subscription = liveQuery(queryFn).subscribe({
      next: (data) => callback(Array.isArray(data) ? data : []),
      error: (error) => console.error('DataService subscription error', error)
    });

    return () => subscription.unsubscribe();
  }

  async hydrateCollection(entityType, fetcher, { orgId, force = false, staleTime = 5 * 60 * 1000 } = {}) {
    if (typeof fetcher !== 'function') {
      throw new Error('hydrateCollection requires a fetcher function');
    }

    await this.ensureInitialized();

    if (!navigator.onLine && !force) {
      return { skipped: true, reason: 'offline' };
    }

    const metaKey = this._getLastSyncKey(entityType, orgId);
    const lastSync = await this.getMetadata(metaKey);
    const now = Date.now();

    if (!force && lastSync && lastSync.timestamp && now - lastSync.timestamp < staleTime) {
      return { skipped: true, reason: 'fresh' };
    }

    const remoteRecords = await fetcher();
    if (!Array.isArray(remoteRecords)) {
      throw new Error('Fetcher must return an array of records');
    }

    await this.upsertFromServer(entityType, remoteRecords, { orgId });
    await this.setMetadata(metaKey, { timestamp: now, count: remoteRecords.length });

    return { success: true, count: remoteRecords.length };
  }

  async replaceCollection(entityType, records = []) {
    await this.ensureInitialized();
    const { table } = this.getEntityConfig(entityType);
    await db.transaction('rw', table, async () => {
      await table.clear();
      if (!Array.isArray(records) || !records.length) return;
      await table.bulkAdd(records.map((record) => ({
        ...record,
        syncStatus: record.syncStatus || SYNC_STATUS.SYNCED,
        dataHash: record.dataHash || hashData(record),
        localModifiedAt: record.localModifiedAt || Date.now()
      })));
    });
  }

  async clearEntity(entityType) {
    await this.ensureInitialized();
    const { table } = this.getEntityConfig(entityType);
    await table.clear();
  }

  async getMetadata(key) {
    await this.ensureInitialized();
    return db.getMetadata(key);
  }

  async setMetadata(key, value) {
    await this.ensureInitialized();
    return db.setMetadata(key, value);
  }

  async removeMetadata(key) {
    await this.ensureInitialized();
    return db.metadata.delete(key);
  }

  _getLastSyncKey(entityType, orgId) {
    return `${METADATA_LAST_SYNC_PREFIX}${entityType}${orgId ? `:${orgId}` : ''}`;
  }

  _resolveServerTimestamp(record, fallbackRecord, defaultValue) {
    if (!record) return fallbackRecord?.serverModifiedAt || defaultValue;
    const serverTs = record.updatedDate || record.updatedAt || record.modifiedAt || record.serverModifiedAt;
    if (!serverTs) return fallbackRecord?.serverModifiedAt || defaultValue;
    const ts = typeof serverTs === 'number' ? serverTs : Date.parse(serverTs);
    return Number.isFinite(ts) ? ts : fallbackRecord?.serverModifiedAt || defaultValue;
  }

  _hasIndex(table, name) {
    if (!table?.schema) return false;
    const { indexes = [] } = table.schema;
    return indexes.some((idx) => idx.name === name);
  }
}

export const dataService = new DataService();


